import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { wellbeingAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Slider } from '../components/ui/slider';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
    Heart,
    TrendingUp,
    Sparkles,
    Zap,
    Target,
    Lightbulb,
    Sun,
    Moon,
    Activity
} from 'lucide-react';
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    AreaChart,
    Area
} from 'recharts';
import { getMoodEmoji, getMoodLabel, formatDate } from '../lib/utils';

const MoodSelector = ({ value, onChange }) => {
    const moods = [1, 2, 3, 4, 5];
    
    return (
        <div className="flex justify-between gap-2">
            {moods.map((mood) => (
                <button
                    key={mood}
                    onClick={() => onChange(mood)}
                    className={`flex-1 py-4 rounded-xl transition-all duration-200 text-3xl ${
                        value === mood
                            ? 'bg-indigo-100 border-2 border-indigo-500 scale-105'
                            : 'bg-stone-50 border-2 border-transparent hover:bg-stone-100'
                    }`}
                    data-testid={`mood-btn-${mood}`}
                >
                    {getMoodEmoji(mood)}
                </button>
            ))}
        </div>
    );
};

const WellnessCard = ({ rec }) => {
    const getIcon = (type) => {
        switch (type) {
            case 'mindfulness': return Sparkles;
            case 'exercise':
            case 'movement': return Activity;
            case 'stress': return Target;
            case 'mood': return Heart;
            case 'social': return TrendingUp;
            default: return Lightbulb;
        }
    };
    const Icon = getIcon(rec.type);

    return (
        <Card className="bento-card-interactive h-full">
            <CardContent className="p-5">
                <div className="w-12 h-12 rounded-xl bg-indigo-50 flex items-center justify-center mb-4">
                    <Icon className="w-6 h-6 text-indigo-600" />
                </div>
                <h4 className="font-semibold text-stone-900 mb-2">{rec.title}</h4>
                <p className="text-sm text-stone-500">{rec.description}</p>
            </CardContent>
        </Card>
    );
};

export default function WellbeingPage() {
    const [moodHistory, setMoodHistory] = useState([]);
    const [recommendations, setRecommendations] = useState([]);
    const [loading, setLoading] = useState(true);
    
    // Mood entry state
    const [mood, setMood] = useState(3);
    const [energy, setEnergy] = useState(3);
    const [stress, setStress] = useState(3);
    const [notes, setNotes] = useState('');
    const [submittingMood, setSubmittingMood] = useState(false);
    const [lastInsight, setLastInsight] = useState('');

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            setLoading(true);
            const [moodRes, recsRes] = await Promise.all([
                wellbeingAPI.getMoodHistory(30),
                wellbeingAPI.getRecommendations()
            ]);
            setMoodHistory(moodRes.data || []);
            setRecommendations(recsRes.data?.recommendations || []);
            if (moodRes.data?.[0]?.ai_insight) {
                setLastInsight(moodRes.data[0].ai_insight);
            }
        } catch (error) {
            console.error('Failed to fetch data:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmitMood = async () => {
        try {
            setSubmittingMood(true);
            const response = await wellbeingAPI.logMood({
                mood,
                energy_level: energy,
                stress_level: stress,
                notes: notes || null
            });
            setLastInsight(response.data.ai_insight || '');
            setNotes('');
            await fetchData();
        } catch (error) {
            console.error('Failed to submit mood:', error);
        } finally {
            setSubmittingMood(false);
        }
    };

    if (loading) {
        return (
            <div className="p-8 flex items-center justify-center min-h-screen">
                <div className="text-center">
                    <div className="w-12 h-12 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin mx-auto mb-4" />
                    <p className="text-stone-500">Loading well-being data...</p>
                </div>
            </div>
        );
    }

    const chartData = [...moodHistory].reverse().map(m => ({
        date: m.timestamp,
        mood: m.mood,
        energy: m.energy_level,
        stress: m.stress_level
    }));

    const avgMood = moodHistory.length > 0
        ? moodHistory.reduce((sum, m) => sum + m.mood, 0) / moodHistory.length
        : 0;
    const avgEnergy = moodHistory.length > 0
        ? moodHistory.reduce((sum, m) => sum + m.energy_level, 0) / moodHistory.length
        : 0;
    const avgStress = moodHistory.length > 0
        ? moodHistory.reduce((sum, m) => sum + m.stress_level, 0) / moodHistory.length
        : 0;
    
    const hasData = moodHistory.length > 0;

    return (
        <div className="p-8" data-testid="wellbeing-page">
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
            >
                {/* Header */}
                <div className="mb-8">
                    <h1 className="text-3xl font-bold font-heading text-stone-900 flex items-center gap-3">
                        <Heart className="w-8 h-8 text-coral-DEFAULT" />
                        Well-being Center
                    </h1>
                    <p className="text-stone-500 mt-1">
                        Track your mood, manage stress, and discover wellness resources.
                    </p>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                    <Card className="bento-card">
                        <CardContent className="p-6 flex items-center gap-4">
                            <div className="text-4xl">{hasData ? getMoodEmoji(Math.round(avgMood)) : '📊'}</div>
                            <div>
                                <p className="text-sm text-stone-500">Avg. Mood</p>
                                <p className="text-2xl font-bold font-heading text-stone-900">
                                    {hasData ? `${avgMood.toFixed(1)}/5` : 'No data'}
                                </p>
                                <p className="text-xs text-stone-400">{hasData ? getMoodLabel(Math.round(avgMood)) : 'Start logging'}</p>
                            </div>
                        </CardContent>
                    </Card>
                    <Card className="bento-card">
                        <CardContent className="p-6 flex items-center gap-4">
                            <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center">
                                <Zap className="w-6 h-6 text-amber-600" />
                            </div>
                            <div>
                                <p className="text-sm text-stone-500">Avg. Energy</p>
                                <p className="text-2xl font-bold font-heading text-amber-600">
                                    {hasData ? `${avgEnergy.toFixed(1)}/5` : 'No data'}
                                </p>
                            </div>
                        </CardContent>
                    </Card>
                    <Card className="bento-card">
                        <CardContent className="p-6 flex items-center gap-4">
                            <div className="w-12 h-12 rounded-xl bg-coral-light/30 flex items-center justify-center">
                                <Activity className="w-6 h-6 text-coral-DEFAULT" />
                            </div>
                            <div>
                                <p className="text-sm text-stone-500">Avg. Stress</p>
                                <p className="text-2xl font-bold font-heading text-coral-DEFAULT">
                                    {hasData ? `${avgStress.toFixed(1)}/5` : 'No data'}
                                </p>
                            </div>
                        </CardContent>
                    </Card>
                </div>

                {/* Daily Check-in Card */}
                <Card className="mb-8 bento-card">
                    <CardHeader>
                        <CardTitle className="font-heading flex items-center gap-2">
                            <Heart className="w-5 h-5 text-coral-DEFAULT" />
                            Daily Check-in
                        </CardTitle>
                        <CardDescription>How are you feeling today?</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-6">
                        <div>
                            <Label className="text-sm font-medium text-stone-700 mb-3 block">Mood</Label>
                            <MoodSelector value={mood} onChange={setMood} />
                            <p className="text-center text-sm text-stone-500 mt-2">
                                {getMoodLabel(mood)}
                            </p>
                        </div>

                        <div className="grid grid-cols-2 gap-6">
                            <div>
                                <Label className="text-sm font-medium text-stone-700 mb-3 block">
                                    Energy Level: {energy}/5
                                </Label>
                                <Slider
                                    value={[energy]}
                                    onValueChange={([v]) => setEnergy(v)}
                                    min={1}
                                    max={5}
                                    step={1}
                                    className="w-full"
                                    data-testid="energy-slider"
                                />
                            </div>
                            <div>
                                <Label className="text-sm font-medium text-stone-700 mb-3 block">
                                    Stress Level: {stress}/5
                                </Label>
                                <Slider
                                    value={[stress]}
                                    onValueChange={([v]) => setStress(v)}
                                    min={1}
                                    max={5}
                                    step={1}
                                    className="w-full"
                                    data-testid="stress-slider"
                                />
                            </div>
                        </div>

                        <div>
                            <Label className="text-sm font-medium text-stone-700 mb-2 block">
                                Notes (optional)
                            </Label>
                            <Textarea
                                placeholder="What's on your mind today?"
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                                className="resize-none rounded-xl"
                                rows={3}
                                data-testid="mood-notes-input"
                            />
                        </div>

                        <Button
                            onClick={handleSubmitMood}
                            disabled={submittingMood}
                            className="w-full h-12 rounded-xl bg-indigo-600 hover:bg-indigo-700"
                            data-testid="submit-mood-btn"
                        >
                            {submittingMood ? (
                                <span className="flex items-center gap-2">
                                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                    Saving...
                                </span>
                            ) : (
                                'Log Today\'s Mood'
                            )}
                        </Button>

                        {lastInsight && (
                            <motion.div
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                className="p-4 bg-indigo-50 rounded-xl border border-indigo-100"
                            >
                                <div className="flex items-start gap-3">
                                    <Sparkles className="w-5 h-5 text-indigo-600 flex-shrink-0 mt-0.5" />
                                    <div>
                                        <p className="text-sm font-medium text-indigo-900 mb-1">AI Insight</p>
                                        <p className="text-sm text-indigo-700">{lastInsight}</p>
                                    </div>
                                </div>
                            </motion.div>
                        )}
                    </CardContent>
                </Card>

                {/* Charts */}
                <Card className="mb-8 bento-card">
                    <CardHeader>
                        <CardTitle className="font-heading">Well-being Trends</CardTitle>
                        <CardDescription>Your mood, energy, and stress levels over the past 30 days</CardDescription>
                    </CardHeader>
                    <CardContent>
                        <Tabs defaultValue="mood">
                            <TabsList className="mb-4">
                                <TabsTrigger value="mood">Mood</TabsTrigger>
                                <TabsTrigger value="energy">Energy</TabsTrigger>
                                <TabsTrigger value="stress">Stress</TabsTrigger>
                                <TabsTrigger value="all">All</TabsTrigger>
                            </TabsList>
                            <TabsContent value="mood">
                                <div className="h-64">
                                    {chartData.length > 0 ? (
                                        <ResponsiveContainer width="100%" height="100%">
                                            <AreaChart data={chartData}>
                                                <defs>
                                                    <linearGradient id="moodGradient" x1="0" y1="0" x2="0" y2="1">
                                                        <stop offset="5%" stopColor="#4F46E5" stopOpacity={0.3} />
                                                        <stop offset="95%" stopColor="#4F46E5" stopOpacity={0} />
                                                    </linearGradient>
                                                </defs>
                                                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
                                                <XAxis
                                                    dataKey="date"
                                                    tick={{ fontSize: 12, fill: '#78716c' }}
                                                    tickFormatter={(val) => new Date(val).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                                />
                                                <YAxis domain={[1, 5]} tick={{ fontSize: 12, fill: '#78716c' }} ticks={[1, 2, 3, 4, 5]} />
                                                <Tooltip
                                                    content={({ active, payload }) => {
                                                        if (active && payload?.[0]) {
                                                            return (
                                                                <div className="glass p-3 rounded-xl">
                                                                    <p className="text-sm font-medium">
                                                                        Mood: {payload[0].value}/5 {getMoodEmoji(payload[0].value)}
                                                                    </p>
                                                                    <p className="text-xs text-stone-500">
                                                                        {formatDate(payload[0].payload.date)}
                                                                    </p>
                                                                </div>
                                                            );
                                                        }
                                                        return null;
                                                    }}
                                                />
                                                <Area type="monotone" dataKey="mood" stroke="#4F46E5" strokeWidth={2} fill="url(#moodGradient)" />
                                            </AreaChart>
                                        </ResponsiveContainer>
                                    ) : (
                                        <div className="flex items-center justify-center h-full">
                                            <p className="text-stone-500">Start tracking your mood to see trends!</p>
                                        </div>
                                    )}
                                </div>
                            </TabsContent>
                            <TabsContent value="energy">
                                <div className="h-64">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <AreaChart data={chartData}>
                                            <defs>
                                                <linearGradient id="energyGradient" x1="0" y1="0" x2="0" y2="1">
                                                    <stop offset="5%" stopColor="#F59E0B" stopOpacity={0.3} />
                                                    <stop offset="95%" stopColor="#F59E0B" stopOpacity={0} />
                                                </linearGradient>
                                            </defs>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
                                            <XAxis dataKey="date" tick={{ fontSize: 12, fill: '#78716c' }} tickFormatter={(val) => new Date(val).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} />
                                            <YAxis domain={[1, 5]} tick={{ fontSize: 12, fill: '#78716c' }} ticks={[1, 2, 3, 4, 5]} />
                                            <Tooltip />
                                            <Area type="monotone" dataKey="energy" stroke="#F59E0B" strokeWidth={2} fill="url(#energyGradient)" />
                                        </AreaChart>
                                    </ResponsiveContainer>
                                </div>
                            </TabsContent>
                            <TabsContent value="stress">
                                <div className="h-64">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <AreaChart data={chartData}>
                                            <defs>
                                                <linearGradient id="stressGradient" x1="0" y1="0" x2="0" y2="1">
                                                    <stop offset="5%" stopColor="#F47266" stopOpacity={0.3} />
                                                    <stop offset="95%" stopColor="#F47266" stopOpacity={0} />
                                                </linearGradient>
                                            </defs>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
                                            <XAxis dataKey="date" tick={{ fontSize: 12, fill: '#78716c' }} tickFormatter={(val) => new Date(val).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} />
                                            <YAxis domain={[1, 5]} tick={{ fontSize: 12, fill: '#78716c' }} ticks={[1, 2, 3, 4, 5]} />
                                            <Tooltip />
                                            <Area type="monotone" dataKey="stress" stroke="#F47266" strokeWidth={2} fill="url(#stressGradient)" />
                                        </AreaChart>
                                    </ResponsiveContainer>
                                </div>
                            </TabsContent>
                            <TabsContent value="all">
                                <div className="h-64">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <LineChart data={chartData}>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
                                            <XAxis dataKey="date" tick={{ fontSize: 12, fill: '#78716c' }} tickFormatter={(val) => new Date(val).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} />
                                            <YAxis domain={[1, 5]} tick={{ fontSize: 12, fill: '#78716c' }} ticks={[1, 2, 3, 4, 5]} />
                                            <Tooltip />
                                            <Line type="monotone" dataKey="mood" stroke="#4F46E5" strokeWidth={2} dot={{ fill: '#4F46E5' }} />
                                            <Line type="monotone" dataKey="energy" stroke="#F59E0B" strokeWidth={2} dot={{ fill: '#F59E0B' }} />
                                            <Line type="monotone" dataKey="stress" stroke="#F47266" strokeWidth={2} dot={{ fill: '#F47266' }} />
                                        </LineChart>
                                    </ResponsiveContainer>
                                </div>
                                <div className="flex justify-center gap-6 mt-4">
                                    <div className="flex items-center gap-2">
                                        <div className="w-3 h-3 rounded-full bg-indigo-600" />
                                        <span className="text-sm text-stone-600">Mood</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <div className="w-3 h-3 rounded-full bg-amber-500" />
                                        <span className="text-sm text-stone-600">Energy</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <div className="w-3 h-3 rounded-full bg-coral-DEFAULT" />
                                        <span className="text-sm text-stone-600">Stress</span>
                                    </div>
                                </div>
                            </TabsContent>
                        </Tabs>
                    </CardContent>
                </Card>

                {/* Recommendations */}
                <div>
                    <h2 className="text-xl font-bold font-heading text-stone-900 mb-4">
                        Personalized Recommendations
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        {recommendations.map((rec, index) => (
                            <WellnessCard key={index} rec={rec} />
                        ))}
                    </div>
                </div>
            </motion.div>
        </div>
    );
}
